import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useState } from "react";

/**
 * The practice view (client-facing training dashboard): the roster, each
 * person's pathway, and progress — for owners, admins, and assigners.
 * Aggregate training facts only; nothing here is clinical.
 */
export function PracticePage() {
  const api = useApi();
  const [org, setOrg] = useState<string | undefined>(undefined);
  const me = useLoad((a) => a.me());
  const orgs = (me.data?.memberships ?? []).filter((m) => m.role === "owner" || m.role === "admin" || m.permissions.includes("learning:assign"));
  const organizationId = org ?? orgs[0]?.organizationId;
  const data = useLoad(async (a) => (organizationId ? Promise.all([a.catalog(), a.practice.roster(organizationId)]) : null), [organizationId]);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  if (me.state === "error") return <Status kind="error" label="Couldn't load your practice." />;
  if (me.data && orgs.length === 0) return <Status kind="info" label="Your account doesn't administer a practice's training." />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const [cat, roster] = data.data;

  async function assign(userId: string) {
    const pathwayId = choice[userId];
    if (!pathwayId || !organizationId) return;
    setBusy(userId);
    setNote(null);
    try {
      const res = await api.practice.assign(organizationId, userId, pathwayId);
      setNote(`Assigned ${cat.pathways.find((p) => p.id === pathwayId)?.title ?? pathwayId}; ${res.enrollmentsCreated} module${res.enrollmentsCreated === 1 ? "" : "s"} added.`);
      data.reload();
    } catch {
      setNote("Couldn't assign that pathway.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="mb-2 text-h1 font-semibold text-anchor">{roster.organization.name}</h1>
          <p className="max-w-[68ch] text-ink-secondary">Who is enrolled, what each person is assigned, and how far along they are. Assign a pathway to offer someone the sections for the work they do; completion never grants clinical authority.</p>
        </div>
        {orgs.length > 1 && (
          <Select label="Practice" value={organizationId} onChange={(e) => setOrg(e.target.value)} options={orgs.map((o) => ({ value: o.organizationId, label: o.organizationName }))} />
        )}
      </div>
      {note && (
        <div className="mb-4">
          <Status kind="info" label={note} />
        </div>
      )}
      <Card>
        <Table caption={`${roster.members.length} active member${roster.members.length === 1 ? "" : "s"}`}>
          <thead>
            <tr>
              <Th>Person</Th>
              <Th>Pathways</Th>
              <Th>Progress</Th>
              <Th>Assign</Th>
            </tr>
          </thead>
          <tbody>
            {roster.members.map((m) => (
              <tr key={m.userId}>
                <Td>
                  <div className="font-medium">{m.name ?? m.email}</div>
                  <div className="text-small text-ink-secondary">
                    {m.email} · {m.role}
                  </div>
                </Td>
                <Td>{m.assignments.length ? m.assignments.map((a) => cat.pathways.find((p) => p.id === a.pathwayId)?.title ?? a.pathwayId).join(", ") : <span className="text-ink-secondary">None</span>}</Td>
                <Td>
                  <div>
                    {m.summary.classesCompleted} of {m.summary.classesTotal} classes
                  </div>
                  <div className="text-small text-ink-secondary">
                    {m.summary.modulesEnrolled} modules · {m.summary.checksPassed} checks passed
                  </div>
                </Td>
                <Td>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                    <Select
                      label="Pathway"
                      className="max-w-[260px]"
                      value={choice[m.userId] ?? ""}
                      placeholder="Choose…"
                      onChange={(e) => setChoice((c) => ({ ...c, [m.userId]: e.target.value }))}
                      options={cat.pathways.map((p) => ({ value: p.id, label: p.title }))}
                    />
                    <Button variant="secondary" surface="card" onClick={() => void assign(m.userId)} disabled={!choice[m.userId] || busy === m.userId}>
                      {busy === m.userId ? "Assigning…" : "Assign"}
                    </Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}
